import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import {
  ArrowRight, Check, Star, ShoppingBag, ChevronDown,
  MapPin, Clock, Shield, Truck, Users, Calendar, Heart,
  Zap, Award, MessageCircle, Camera, Music, Utensils
} from 'lucide-react';
import MobileStickyBar from '../components/common/MobileStickyBar';
import ContactModal from '../components/common/ContactModal';
import CallButton from '../components/common/CallButton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── CONFIGS COMPLÈTES PAR THÈME ─────────────────────────────────────────────
const THEME_CONFIGS = {

  // ═══════════════════════════════════════════════════════════ MARIAGE
  mariage: {
    title: 'Mariage',
    headline: 'Votre Jour J, inoubliable dans le Lot et l\'Aveyron',
    subheadline: 'Chaque détail compte. Nous créons l\'ambiance dont vous rêvez.',
    desc: 'Photobooth premium, arche ballons sur-mesure, bars gourmands, coin enfants clé en main, brasero, décoration personnalisée. Show Événement compose votre mariage de A à Z dans le Lot, l\'Aveyron et le Cantal.',
    hero_image: 'https://images.unsplash.com/photo-1581720848209-9721f8fa30ff?w=1400&q=85',
    hero_image2: 'https://images.unsplash.com/photo-1727764894973-28e7283a600c?w=800&q=80',
    accent: '#D4AF37',
    accent_bg: 'rgba(212,175,55,0.08)',
    accent_light: 'rgba(212,175,55,0.05)',
    badge: '⭐ Spécialiste mariage Lot',
    pack_id: 'pack-mariage-premium',
    univers_id: 'mariage',
    categories: ['cat-photo', 'cat-deco'],

    stats: [
      { value: '50+', label: 'Mariages organisés' },
      { value: '4.6★', label: 'Note Google' },
      { value: '1h30', label: 'Rayon Figeac' },
      { value: '24h/24', label: 'Disponibles le Jour J' },
    ],

    features: [
      { icon: Camera, text: 'Cabine photobooth HD + impressions instantanées pour vos invités' },
      { icon: Heart, text: 'Arche ballons sur-mesure aux couleurs de votre mariage' },
      { icon: Utensils, text: 'Bar à bonbons, crêpes, barbe à papa, brasero convivial' },
      { icon: Users, text: 'Coin enfants clé en main — structure gonflable et animations' },
      { icon: Award, text: 'Décoration personnalisée — panneau bienvenue, guirlandes, nappes' },
      { icon: Music, text: 'Sono & éclairage LED d\'ambiance sur demande' },
      { icon: Zap, text: 'Disponibles 24h/24 pendant votre événement en cas de besoin' },
      { icon: Truck, text: 'Installation la veille possible — reprise en soirée ou le lendemain' },
    ],

    etapes: [
      { num: '01', title: 'Configurez en 2 min', desc: 'Composez votre mariage en ligne : date, lieu, animations souhaitées. Devis instantané.' },
      { num: '02', title: 'Validation & acompte', desc: 'Signez votre devis en ligne et réglez 30% pour bloquer votre date. Simple et sécurisé.' },
      { num: '03', title: 'Installation le Jour J', desc: 'Notre équipe arrive, installe tout et reste disponible pendant toute la célébration.' },
      { num: '04', title: 'Reprise du matériel', desc: 'Nous récupérons tout après votre soirée — même de nuit si nécessaire.' },
    ],

    faq: [
      { q: 'Dans quelle zone intervenez-vous pour les mariages ?', a: 'Dans un rayon de 1h30 autour de Figeac : Lot (46), Aveyron (12), Cantal (15), Corrèze (19). Figeac, Cahors, Saint-Céré, Gourdon, Souillac et bien d\'autres communes.' },
      { q: 'Peut-on réserver le photobooth pour un mariage ?', a: 'Absolument ! Notre cabine photobooth est notre produit le plus demandé pour les mariages. Impressions HD en 10 secondes, fond personnalisable, accessoires inclus. Nous recommandons de réserver 6 semaines à l\'avance.' },
      { q: 'Restez-vous sur place pendant le mariage ?', a: 'Oui ! Nous proposons une prestation de présence, surveillance et animation sur place. Et nous sommes joignables 24h/24 durant l\'événement en cas de besoin ou panne.' },
      { q: 'Peut-on faire une installation la veille ?', a: 'Oui, pour les mariages nous pouvons livrer et installer la veille, pour que tout soit parfait le Jour J. À préciser au moment de la réservation.' },
      { q: 'Proposez-vous des forfaits mariage complets ?', a: 'Oui ! Nous créons des forfaits personnalisés — photobooth + animations gourmandes + coin enfants + décoration. Demandez votre devis gratuit en ligne.' },
      { q: 'Récupérez-vous le matériel en soirée ?', a: 'Absolument. Après votre soirée, nous récupérons tout le matériel — même de nuit si besoin. Aucune contrainte pour vous le lendemain.' },
    ],

    gallery: [
      'https://images.unsplash.com/photo-1581720848209-9721f8fa30ff?w=600&q=80',
      'https://images.unsplash.com/photo-1581745073351-e053d7d73694?w=600&q=80',
      'https://images.unsplash.com/photo-1581720848095-2b72764b08a2?w=600&q=80',
      'https://images.unsplash.com/photo-1727764894973-28e7283a600c?w=600&q=80',
    ],

    testimonials: [
      { name: 'Rachel C.', note: 5, text: '"Super ! Je le referais pour d\'autres occasions. Ce week-end pour notre mariage, apprécié de tout le monde."' },
      { name: 'Laurent Andrzejak', note: 5, text: '"Nous avons loué le photobooth et ça a fait son effet ! Tout le monde a adoré. Guillaume a sauvé nos boissons grâce à sa chambre froide louée en urgence. Je recommande à 100%."' },
    ],

    cta_title: 'Prêt à créer le mariage de vos rêves dans le Lot ?',
    cta_desc: 'Devis gratuit en 2 minutes. Livraison, installation et reprise incluses. Dans un rayon de 1h30 autour de Figeac.',
    seo_title: 'Mariage Lot (46) — Photobooth, décoration, animations | Show Événement',
  },

  // ═══════════════════════════════════════════════════════════ ANNIVERSAIRE ENFANT
  anniversaire: {
    title: 'Anniversaire enfant',
    headline: 'La fête parfaite pour votre enfant dans le Lot et l\'Aveyron',
    subheadline: 'Des souvenirs magiques, une organisation zéro stress.',
    desc: 'Châteaux gonflables, barbe à papa, popcorn, bar à bonbons, arche ballons, déco thématique. Toutes les animations pour un anniversaire inoubliable — Figeac, Lot, Aveyron, Cantal.',
    hero_image: 'https://images.unsplash.com/photo-1765947378663-9e7ad34ca310?w=1400&q=85',
    hero_image2: 'https://images.unsplash.com/photo-1765947389633-461a4af7712b?w=800&q=80',
    accent: '#F4A7B2',
    accent_bg: 'rgba(244,167,178,0.1)',
    accent_light: 'rgba(244,167,178,0.05)',
    badge: '🎉 Très populaire',
    pack_id: 'pack-anniversaire-enfant',
    univers_id: 'anniversaire_enfant',
    categories: ['cat-jeux-gonflables', 'cat-food'],

    stats: [
      { value: '200+', label: 'Fêtes organisées' },
      { value: '3-12', label: 'Ans — enfants ravis' },
      { value: '30 min', label: 'Installation rapide' },
      { value: '0€', label: 'Frais surprise' },
    ],

    features: [
      { icon: Heart, text: 'Châteaux gonflables licorne, pirates, princesse — certifiés et assurés' },
      { icon: Zap, text: 'Parcours d\'obstacles et structures géantes pour les plus grands' },
      { icon: Utensils, text: 'Barbe à papa, popcorn, crêpes, bar à bonbons thématique' },
      { icon: Award, text: 'Arche ballons aux couleurs du thème de votre enfant' },
      { icon: Users, text: 'Mini golf, jeux en bois géants adaptés aux enfants' },
      { icon: Camera, text: 'Photobooth kids avec accessoires rigolos et impressions' },
      { icon: Truck, text: 'Livraison, installation et reprise incluses — rien à faire !' },
      { icon: Zap, text: 'Disponibles 24h/24 pendant la fête en cas de besoin ou panne' },
    ],

    etapes: [
      { num: '01', title: 'Choisissez les animations', desc: 'Configurez en ligne ou appelez-nous. Nous vous conseillons selon l\'âge et le nombre d\'enfants.' },
      { num: '02', title: 'Confirmez la date', desc: 'Devis en ligne, signature électronique, acompte 30%. Votre date est bloquée immédiatement.' },
      { num: '03', title: 'On installe tout', desc: 'Notre équipe arrive 1h avant la fête, installe tout et vérifie la sécurité de chaque structure.' },
      { num: '04', title: 'On repart en soirée', desc: 'Nous récupérons tout après la fête. Votre jardin est comme avant — propre et rangé.' },
    ],

    faq: [
      { q: 'À partir de quel âge peut-on utiliser les châteaux gonflables ?', a: 'Nos structures sont adaptées aux enfants de 3 à 12 ans. Chaque château indique l\'âge recommandé. Nous avons aussi des structures pour les plus grands et les adultes !' },
      { q: 'Faut-il un espace extérieur ou peut-on installer en intérieur ?', a: 'Les deux sont possibles ! Nos gonflables s\'installent en intérieur (salle des fêtes, gymnase, grange) comme en extérieur. Surface minimale selon le modèle : en moyenne 8x8m.' },
      { q: 'Que se passe-t-il en cas de pluie ou vent fort ?', a: 'Nous vous contactons la veille si météo difficile. En cas de vent fort (>50km/h), nous déconseillons l\'usage pour des raisons de sécurité. Une solution alternative vous sera proposée.' },
      { q: 'Peut-on réserver la veille pour un anniversaire le dimanche ?', a: 'Oui, pour les anniversaires nous pouvons livrer le samedi après-midi pour une fête le dimanche. Précisez-le lors de votre devis.' },
      { q: 'Combien de temps pour installer un château gonflable ?', a: 'En moyenne 20 à 30 minutes. Notre équipe s\'occupe de tout : gonflage, ancrage, vérification sécurité. Vous n\'avez rien à faire !' },
      { q: 'Proposez-vous des animations gourmandes ?', a: 'Oui ! Barbe à papa, popcorn, machine à crêpes, bar à bonbons personnalisable. Tout est livré avec les consommables pour la durée de votre fête.' },
    ],

    gallery: [
      'https://images.unsplash.com/photo-1765947378663-9e7ad34ca310?w=600&q=80',
      'https://images.unsplash.com/photo-1765947377925-941c12c84331?w=600&q=80',
      'https://images.unsplash.com/photo-1765947389633-461a4af7712b?w=600&q=80',
      'https://images.unsplash.com/photo-1765947379899-37a389330d77?w=600&q=80',
    ],

    testimonials: [
      { name: 'Christillia P.', note: 5, text: '"Super moment pour l\'anniversaire de ma fille, je recommande à 100 % !!!!!!"' },
      { name: 'Christelle L.', note: 5, text: '"Tout était parfait ! Les enfants ont adoré le château gonflable. Livraison ponctuelle et équipe très professionnelle. Merci !"' },
    ],

    cta_title: 'Prêt à organiser la fête de l\'année ?',
    cta_desc: 'Devis gratuit en 2 minutes. Livraison, installation et reprise incluses. Dans un rayon de 1h30 autour de Figeac.',
    seo_title: 'Anniversaire enfant Figeac — Château gonflable, animations | Show Événement',
  },

  // ═══════════════════════════════════════════════════════════ ENTREPRISE & CE
  entreprise: {
    title: 'Entreprise & CE',
    headline: 'Des événements professionnels qui marquent les esprits',
    subheadline: 'Tarifs CE avantageux · Facturation pro · Lot, Aveyron, Cantal',
    desc: 'Cocktails d\'entreprise, inaugurations, team building, soirées CE, animations commerciales. Tarifs professionnels, facturation avec TVA. Disponible du lundi au dimanche — Lot, Aveyron, Cantal.',
    hero_image: 'https://images.unsplash.com/photo-1768508948485-a7adc1f3427f?w=1400&q=85',
    hero_image2: 'https://images.unsplash.com/photo-1768508948849-b2ee9e074ab1?w=800&q=80',
    accent: '#5DBCBF',
    accent_bg: 'rgba(93,188,191,0.08)',
    accent_light: 'rgba(93,188,191,0.05)',
    badge: '🏢 Tarifs CE & Pro disponibles',
    pack_id: 'pack-entreprise-cocktail',
    univers_id: 'entreprise',
    categories: ['cat-animation', 'cat-mobilier'],

    stats: [
      { value: '-20%', label: 'Tarif CE & Asso' },
      { value: 'TVA', label: 'Facturation pro' },
      { value: '7j/7', label: 'Disponibilité' },
      { value: '24h/24', label: 'Pendant l\'événement' },
    ],

    features: [
      { icon: Award, text: 'Tarifs CE et association jusqu\'à -20% sur toute la gamme' },
      { icon: Shield, text: 'Facturation professionnelle avec TVA — devis et bon de commande' },
      { icon: Camera, text: 'Cabine photobooth HD avec logo entreprise personnalisable' },
      { icon: Music, text: 'Sono, éclairage LED, scène : animation cocktail clé en main' },
      { icon: Utensils, text: 'Bar à bières, espace lounge, mange-debout, nappes pro' },
      { icon: Zap, text: 'Animation blind test, mini golf, escape game, jeux bois géants' },
      { icon: Users, text: 'Présence et animation sur place par notre équipe pendant tout l\'événement' },
      { icon: Clock, text: 'Disponibles en semaine et week-end — intervention 24h/24 si besoin' },
    ],

    etapes: [
      { num: '01', title: 'Brief & devis pro', desc: 'Décrivez votre événement. Nous vous envoyons un devis professionnel personnalisé sous 24h.' },
      { num: '02', title: 'Validation & bon de commande', desc: 'Signature électronique, acompte 30%. Facturation pro avec TVA disponible à tout moment.' },
      { num: '03', title: 'Installation & animation', desc: 'Notre équipe s\'occupe de tout : installation, animation, surveillance pendant tout l\'événement.' },
      { num: '04', title: 'Reprise et bilan', desc: 'Récupération du matériel — même en soirée après un cocktail tardif. Bilan et facture finale.' },
    ],

    faq: [
      { q: 'Proposez-vous des tarifs spéciaux pour les comités d\'entreprise ?', a: 'Oui ! Les CE bénéficient de tarifs préférentiels jusqu\'à -20% sur toute notre gamme. Contactez-nous avec votre code CE ou demandez un devis professionnel.' },
      { q: 'Fournissez-vous des factures avec TVA ?', a: 'Absolument. Toutes nos prestations B2B donnent lieu à une facture professionnelle avec TVA déductible. Devis et bon de commande disponibles sur demande.' },
      { q: 'Pouvez-vous intervenir en semaine pour un événement d\'entreprise ?', a: 'Oui, nous sommes disponibles du lundi au dimanche pour les événements professionnels. Inauguration, cocktail de fin d\'année, animation commerciale — nous nous adaptons à votre agenda.' },
      { q: 'Pouvez-vous personnaliser les animations avec notre logo ?', a: 'Oui ! Le photobooth peut afficher votre logo sur les impressions. Les structures peuvent être brandées. Contactez-nous pour discuter de vos besoins de personnalisation.' },
      { q: 'Restez-vous sur place pendant le cocktail ?', a: 'Toujours. Nous proposons une prestation de présence, surveillance et animation sur place. Notre équipe est disponible 24h/24 pendant votre événement pour intervenir en cas de besoin.' },
      { q: 'Récupérez-vous le matériel après une soirée tardive ?', a: 'Oui, nous organisons des reprises nocturnes après les cocktails et soirées d\'entreprise. Aucune contrainte pour vous le lendemain matin au bureau.' },
    ],

    gallery: [
      'https://images.unsplash.com/photo-1768508948485-a7adc1f3427f?w=600&q=80',
      'https://images.unsplash.com/photo-1768508948849-b2ee9e074ab1?w=600&q=80',
      'https://images.unsplash.com/photo-1768508662436-c0ab87329d17?w=600&q=80',
      'https://images.unsplash.com/photo-1727764894973-28e7283a600c?w=600&q=80',
    ],

    testimonials: [
      { name: 'Entreprise du Lot', note: 5, text: '"Animation parfaite pour notre cocktail d\'inauguration. Équipe professionnelle, matériel impeccable. Nos collaborateurs ont adoré le photobooth brandé à nos couleurs."' },
      { name: 'CE Figeac', note: 5, text: '"Super prestation pour notre soirée CE. Tarif avantageux, facturation pro, équipe disponible et réactive. Je recommande sans hésiter."' },
    ],

    cta_title: 'Organisez un événement professionnel mémorable dans le Lot',
    cta_desc: 'Devis pro en 24h. Facturation TVA. Disponible en semaine et week-end. Dans un rayon de 1h30 autour de Figeac.',
    seo_title: 'Animation entreprise Lot (46) — CE, cocktail, team building | Show Événement',
  },
};

// ─── COMPOSANT FAQ ────────────────────────────────────────────────────────────
function FAQItem({ q, a, accent }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl overflow-hidden cursor-pointer"
      style={{ border: `1px solid ${open ? accent + '40' : 'var(--border)'}`, background: '#FFFFFF', transition: 'border-color 0.2s' }}
    >
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left gap-4"
        onClick={() => setOpen(!open)}
      >
        <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{q}</span>
        <ChevronDown size={16} style={{ color: accent, flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div className="px-5 pb-4">
          <p className="text-sm" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>{a}</p>
        </div>
      )}
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────────
export default function ThematiquePage({ theme = 'mariage' }) {
  const config = THEME_CONFIGS[theme] || THEME_CONFIGS.mariage;
  const [products, setProducts] = useState([]);
  const [pack, setPack] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    fetch(`${BACKEND}/api/configurateur/products/${config.univers_id}?profile=particulier&limit=8`)
      .then(r => r.json())
      .then(d => setProducts(Array.isArray(d) ? d : []))
      .catch(() => {});

    fetch(`${BACKEND}/api/packs/${config.pack_id}`)
      .then(r => r.json())
      .then(d => setPack(d))
      .catch(() => {});
  // eslint-disable-next-line
  }, [theme]);

  const accent = config.accent;
  const accentBg = config.accent_bg;
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* ══════════════════════════════════ HERO ══════════════════════════════ */}
      <section className="relative overflow-hidden" style={{ minHeight: '70vh' }}>
        <img
          src={config.hero_image}
          alt={config.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(11,30,32,0.82) 0%, rgba(11,30,32,0.45) 60%, transparent 100%)'
        }} />

        <div className="relative container-ose flex flex-col justify-end pb-16" style={{ minHeight: '70vh', paddingTop: '5rem' }}>
          {/* Badge */}
          <span className="inline-block w-fit px-4 py-2 rounded-full text-xs font-bold mb-5"
            style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}55`, backdropFilter: 'blur(8px)' }}>
            {config.badge}
          </span>

          {/* Titre */}
          <h1 className="font-display text-4xl sm:text-6xl font-bold mb-3"
            style={{ color: '#FFFFFF', letterSpacing: '-0.03em', maxWidth: '780px', lineHeight: '1.05' }}>
            {config.headline}
          </h1>
          <p className="text-base sm:text-lg mb-3 max-w-xl"
            style={{ color: accent, fontWeight: 600 }}>
            {config.subheadline}
          </p>
          <p className="text-sm mb-8 max-w-2xl"
            style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '1.75' }}>
            {config.desc}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/creer-evenement"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-bold"
              style={{ background: accent, color: '#0B1E20', boxShadow: `0 12px 36px ${accent}45` }}>
              Créer mon {config.title.toLowerCase()} <ArrowRight size={18} />
            </Link>
            <button
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-semibold"
              style={{ background: 'rgba(255,255,255,0.12)', color: '#FFFFFF', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer' }}>
              <MessageCircle size={16} /> Nous contacter
            </button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ STATS ═════════════════════════════ */}
      <section style={{ background: '#0B1E20', borderBottom: `3px solid ${accent}` }}>
        <div className="container-ose">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-white/10">
            {config.stats.map((s, i) => (
              <div key={i} className="py-6 px-4 text-center">
                <p className="font-display text-2xl sm:text-3xl font-bold mb-1" style={{ color: accent }}>{s.value}</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ CE QU'ON INCLUT ═══════════════════ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-3"
              style={{ background: accentBg, color: accent }}>
              Ce qui est inclus
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Pourquoi choisir Show Événement<br />pour votre {config.title.toLowerCase()} ?
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {config.features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex items-start gap-3 p-5 rounded-2xl"
                  style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: accentBg }}>
                    <Icon size={16} style={{ color: accent }} />
                  </div>
                  <p className="text-sm" style={{ color: 'var(--ink)', lineHeight: '1.6' }}>{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ COMMENT ÇA MARCHE ════════════════ */}
      <section className="section-py" style={{ background: accentBg }}>
        <div className="container-ose">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Comment ça marche ?
            </h2>
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>De la réservation à la reprise, on s'occupe de tout</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {config.etapes.map((e, i) => (
              <div key={i} className="relative">
                {i < config.etapes.length - 1 && (
                  <div className="hidden lg:block absolute top-6 left-full w-full h-px z-0"
                    style={{ background: `linear-gradient(90deg, ${accent}60, transparent)`, width: 'calc(100% - 2rem)', marginLeft: '1rem' }} />
                )}
                <div className="relative z-10 p-5 rounded-2xl text-center"
                  style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
                  <span className="inline-block w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mb-3 mx-auto"
                    style={{ background: accent, color: '#0B1E20' }}>
                    {e.num}
                  </span>
                  <h3 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>{e.title}</h3>
                  <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>{e.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ PACK RECOMMANDÉ ══════════════════ */}
      {pack && pack.nom && (
        <section className="section-py section-white">
          <div className="container-ose">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block px-4 py-2 rounded-full text-xs font-bold mb-5"
                  style={{ background: accent, color: '#0B1E20' }}>
                  ⭐ Pack recommandé — Économisez {pack.remise_montant || 0}€
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3"
                  style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                  {pack.nom}
                </h2>
                <p className="text-sm mb-5" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
                  {pack.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {(pack.products || []).slice(0, 5).map((p, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                      <Check size={14} style={{ color: accent, flexShrink: 0 }} />
                      {p.nom}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-5">
                  <div>
                    {pack.total_base > 0 && (
                      <p className="text-xs" style={{ color: 'var(--text-muted)', textDecoration: 'line-through' }}>
                        Prix normal : {pack.total_base}€
                      </p>
                    )}
                    <p className="text-3xl font-bold" style={{ color: accent }}>{pack.total_remise}€</p>
                    {pack.remise_pct > 0 && (
                      <p className="text-xs font-semibold" style={{ color: accent }}>
                        Économisez {pack.remise_pct}%
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => { if (pack.products) { pack.products.forEach(p => addItem(p)); toast.success('Pack ajouté au panier !'); } }}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold"
                    style={{ background: accent, color: '#0B1E20', boxShadow: `0 8px 24px ${accent}35` }}>
                    <ShoppingBag size={16} /> Prendre ce pack
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(pack.products || []).slice(0, 4).map((p, i) => (
                  <Link key={i} to={`/produit/${p.slug || p.id}`} className="card-premium overflow-hidden group">
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt={p.nom}
                        className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    ) : (
                      <div className="w-full h-32 flex items-center justify-center" style={{ background: accentBg }}>
                        <ShoppingBag size={24} style={{ color: accent, opacity: 0.5 }} />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-xs font-semibold line-clamp-1" style={{ color: 'var(--ink)' }}>{p.nom}</p>
                      {p.tarifs?.particulier && (
                        <p className="text-xs font-bold" style={{ color: accent }}>à partir de {p.tarifs.particulier}€</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════ PRODUITS ══════════════════════════ */}
      {products.length > 0 && (
        <section className="section-py" style={{ background: accentBg }}>
          <div className="container-ose">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                  Nos animations {config.title.toLowerCase()}
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Sélection de nos meilleures prestations</p>
              </div>
              <Link to="/catalogue" className="text-sm font-semibold flex items-center gap-1" style={{ color: accent }}>
                Voir tout le catalogue <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {products.slice(0, 8).map(p => (
                <div key={p.id} className="card-premium overflow-hidden group" style={{ background: '#FFFFFF' }}>
                  <Link to={`/produit/${p.slug || p.id}`}>
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt={p.nom}
                        className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    ) : (
                      <div className="w-full h-44 flex items-center justify-center" style={{ background: accentBg }}>
                        <ShoppingBag size={28} style={{ color: accent, opacity: 0.4 }} />
                      </div>
                    )}
                  </Link>
                  <div className="p-4">
                    <Link to={`/produit/${p.slug || p.id}`}>
                      <h3 className="font-semibold text-sm mb-1 line-clamp-2 hover:underline" style={{ color: 'var(--ink)' }}>{p.nom}</h3>
                    </Link>
                    {p.description && (
                      <p className="text-xs line-clamp-2 mb-2" style={{ color: 'var(--text-muted)' }}>{p.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm font-bold" style={{ color: accent }}>
                        à partir de {p.tarifs?.particulier}€
                      </span>
                      <button
                        onClick={() => { addItem(p); toast.success(`${p.nom} ajouté !`); }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-opacity hover:opacity-80"
                        style={{ background: accentBg, color: accent, border: `1px solid ${accent}30` }}>
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/creer-evenement"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-sm font-bold"
                style={{ background: accent, color: '#0B1E20', boxShadow: `0 8px 24px ${accent}35` }}>
                Composer mon événement <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════ GALERIE ═══════════════════════════ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-center" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
            Quelques moments de nos {config.title.toLowerCase()}s
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {config.gallery.map((img, i) => (
              <div key={i} className="overflow-hidden rounded-2xl" style={{ aspectRatio: '1/1' }}>
                <img src={img} alt={`${config.title} ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ TÉMOIGNAGES ═══════════════════════ */}
      <section className="section-py" style={{ background: accentBg }}>
        <div className="container-ose">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-1 mb-3">
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={20} fill={s <= 4 ? accent : 'transparent'} color={accent} />
              ))}
              <span className="ml-2 font-bold text-lg" style={{ color: accent }}>4.6/5</span>
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-bold" style={{ color: 'var(--ink)' }}>
              Ce que disent nos clients
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Avis vérifiés Google</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {config.testimonials.map((t, i) => (
              <div key={i} className="p-6 rounded-2xl"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
                <div className="flex gap-0.5 mb-3">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} size={14} fill={s <= t.note ? accent : 'transparent'} color={accent} />
                  ))}
                </div>
                <p className="text-sm italic mb-4" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>{t.text}</p>
                <p className="text-xs font-bold" style={{ color: 'var(--ink)' }}>— {t.name}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <a href="https://g.page/r/CdEXRDiqSSR_EBM/review" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold"
              style={{ color: accent }}>
              <MessageCircle size={14} /> Laissez votre avis Google →
            </a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ FAQ ═══════════════════════════════ */}
      <section className="section-py section-white">
        <div className="container-ose max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Questions fréquentes
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Tout ce qu'il faut savoir avant de réserver</p>
          </div>
          <div className="space-y-3">
            {config.faq.map((f, i) => (
              <FAQItem key={i} q={f.q} a={f.a} accent={accent} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ ZONE D'INTERVENTION ══════════════ */}
      <section className="section-py" style={{ background: accentBg }}>
        <div className="container-ose">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-4"
                style={{ background: accentBg, color: accent, border: `1px solid ${accent}30` }}>
                Zone d'intervention
              </span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3"
                style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                Dans un rayon de 1h30 autour de Figeac
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
                Basés à Figeac, nous intervenons dans un rayon de 1h30 — Lot (46), Aveyron (12), Cantal (15) et Corrèze (19). Rodez, Aurillac, Cahors, Saint-Céré et toutes les communes du secteur.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {['Figeac', 'Cahors', 'Rodez', 'Aurillac', 'Saint-Céré', 'Decazeville', 'Millau', 'Gourdon', 'Souillac', 'Villefranche'].map(v => (
                  <span key={v} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium"
                    style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                    <MapPin size={10} style={{ color: accent }} /> {v}
                  </span>
                ))}
              </div>
              <Link to="/creer-evenement"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold"
                style={{ background: accent, color: '#0B1E20' }}>
                Vérifier ma zone <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Clock, title: 'Disponibles 24h/24', desc: 'Pendant votre événement en cas de panne ou besoin urgent' },
                { icon: Truck, title: 'Reprise nocturne', desc: 'Récupération du matériel après votre soirée, même de nuit' },
                { icon: Users, title: 'Animation sur place', desc: 'Prestation de présence, surveillance et animation disponible' },
                { icon: Shield, title: 'Matériel certifié', desc: 'Chaque équipement est inspecté et testé avant chaque sortie' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="p-4 rounded-2xl text-center"
                    style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2"
                      style={{ background: accentBg }}>
                      <Icon size={18} style={{ color: accent }} />
                    </div>
                    <p className="text-xs font-bold mb-1" style={{ color: 'var(--ink)' }}>{item.title}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.5' }}>{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════ CTA FINAL ════════════════════════ */}
      <section className="section-py" style={{ background: '#0B1E20' }}>
        <div className="container-ose text-center">
          <h2 className="font-display text-2xl sm:text-4xl font-bold mb-4"
            style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            {config.cta_title}
          </h2>
          <p className="text-sm mb-10 max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {config.cta_desc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/creer-evenement"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-bold"
              style={{ background: accent, color: '#0B1E20', boxShadow: `0 12px 40px ${accent}40` }}>
              Créer mon devis gratuit <ArrowRight size={18} />
            </Link>
            <button
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-semibold"
              style={{ border: '1px solid rgba(255,255,255,0.25)', color: '#FFFFFF', background: 'transparent', cursor: 'pointer' }}>
              <MessageCircle size={16} /> Nous contacter
            </button>
          </div>
          <p className="text-xs mt-6" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Réponse sous 24h · Devis sans engagement · Paiement sécurisé Stripe
          </p>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
      <CallButton />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} sourcePage={`Page ${config.title}`} />
    </div>
  );
}
